import { DASHBOARD_PERMISSIONS, PERMISSIONS as P, type PermissionKey } from '@igoal/shared';
import {
  ArrowLeftRight,
  Bell,
  Boxes,
  ClipboardList,
  Building2,
  ChevronUp,
  PanelLeftClose,
  Scale,
  PanelLeftOpen,
  Search,
  MoreHorizontal,
  Clock,
  Coins,
  Container,
  History,
  Hourglass,
  LayoutDashboard,
  KeyRound,
  ListChecks,
  LogOut,
  Menu,
  MonitorSmartphone,
  Moon,
  Sun,
  SunMoon,
  Package,
  Settings,
  Send,
  Ship,
  Factory,
  FileSpreadsheet,
  ShieldCheck,
  Tags,
  TrendingDown,
  Truck,
  Users,
  Warehouse,
  X,
} from 'lucide-react';
import { Fragment, Suspense, useEffect, useRef, useState, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '../lib/auth';
import { useTheme, type ThemeChoice } from '../lib/theme';
import { CommandPalette, isMac, type Destination } from './CommandPalette';
import { NotificationBell } from './NotificationBell';
import { clsx, Skeleton, SkeletonCards, Tooltip } from './ui';

interface NavItem {
  to: string;
  label: string;
  /** Short name for the phone tab bar */
  short?: string;
  icon: ComponentType<{ className?: string }>;
  /** Shown with this permission, or with any one of a list */
  perm: PermissionKey | PermissionKey[];
  /** Hide when the user also has any of these permissions (another entry covers it) */
  hideIf?: PermissionKey[];
  /** Further permissions the screen needs besides `perm` */
  alsoNeeds?: PermissionKey[];
}

const NAV: { group: string | null; items: NavItem[] }[] = [
  {
    group: null,
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, perm: DASHBOARD_PERMISSIONS },
      { to: '/stock', label: 'Stock & prices', short: 'Stock', icon: Boxes, perm: P.STOCK_VIEW },
      {
        to: '/shop-orders',
        label: 'Shop orders',
        short: 'Shop orders',
        icon: ClipboardList,
        perm: [P.STOCK_REQUESTS_CREATE, P.STOCK_REQUESTS_APPROVE, P.TRANSFERS_DISPATCH, P.BRANCH_ORDERS_SUPPLIER_VIEW],
      },
      { to: '/orders', label: 'Orders', icon: Container, perm: P.CHINA_ORDERS_VIEW },
      // One entry per user: all three open /orders, so showing two would highlight both
      { to: '/orders', label: 'Orders for you', short: 'Orders', icon: Container, perm: P.CHINA_PORTAL_ACCESS, hideIf: [P.CHINA_ORDERS_VIEW] },
      { to: '/order-tracker', label: 'Order tracker', short: 'Tracker', icon: ListChecks, perm: [P.CHINA_ORDERS_VIEW, P.CHINA_PORTAL_ACCESS, P.CHINA_ORDERS_RECEIVE] },
      { to: '/shipments', label: 'Shipments', icon: Ship, perm: [P.SHIPMENTS_VIEW_ALL, P.SHIPMENTS_VIEW_LOCATION, P.SHIPMENTS_MANAGE, P.CHINA_ORDERS_RECEIVE] },
      { to: '/invoices', label: 'Invoices', icon: FileSpreadsheet, perm: [P.SHIPMENTS_VIEW_ALL, P.SHIPMENTS_MANAGE, P.TRANSFERS_DISPATCH] },
      { to: '/transfers', label: 'Transfers', icon: Send, perm: [P.TRANSFERS_DISPATCH, P.TRANSFERS_ACKNOWLEDGE] },
      { to: '/adjustments', label: 'Stock adjustments', short: 'Adjust', icon: Scale, perm: [P.STOCK_ADJUST, P.STOCK_ADJUST_APPROVE] },
    ],
  },
  {
    group: 'Reports',
    items: [
      { to: '/reports/stock-by-location', label: 'Stock by location', icon: Warehouse, perm: P.REPORTS_VIEW },
      { to: '/reports/valuation', label: 'Stock valuation', icon: Coins, perm: P.REPORTS_VIEW, alsoNeeds: [P.ITEMS_COST_VIEW] },
      { to: '/reports/low-stock', label: 'Low stock', icon: TrendingDown, perm: P.REPORTS_VIEW },
      { to: '/reports/slow-stock', label: 'Slow stock', icon: Hourglass, perm: P.REPORTS_VIEW },
      { to: '/reports/movements', label: 'Movement history', icon: ArrowLeftRight, perm: P.REPORTS_VIEW },
      { to: '/reports/on-the-way', label: 'Goods on the way', icon: Truck, perm: P.REPORTS_VIEW },
      { to: '/reports/supplier-performance', label: 'Supplier performance', icon: Factory, perm: P.REPORTS_VIEW, alsoNeeds: [P.CHINA_ORDERS_VIEW] },
      { to: '/reports/freight', label: 'Freight per carton', icon: Ship, perm: [P.SHIPMENTS_VIEW_ALL, P.SHIPMENTS_MANAGE], alsoNeeds: [P.REPORTS_VIEW, P.ITEMS_COST_VIEW] },
    ],
  },
  {
    group: 'Masters',
    items: [
      { to: '/items', label: 'Items', icon: Package, perm: P.ITEMS_VIEW },
      { to: '/suppliers', label: 'Suppliers', icon: Truck, perm: P.SUPPLIERS_VIEW },
      { to: '/categories', label: 'Categories', icon: Tags, perm: P.ITEMS_VIEW },
      { to: '/branches', label: 'Branches', icon: Building2, perm: P.BRANCHES_VIEW },
      { to: '/users', label: 'Users', icon: Users, perm: P.USERS_VIEW },
      { to: '/roles', label: 'Roles & permissions', short: 'Roles', icon: ShieldCheck, perm: P.ROLES_VIEW },
    ],
  },
  {
    group: 'Administration',
    items: [
      { to: '/audit', label: 'Audit log', icon: History, perm: P.AUDIT_VIEW },
      { to: '/settings', label: 'Settings', icon: Settings, perm: P.SETTINGS_MANAGE },
    ],
  },
];

// wrap `can`: it takes several keys, so passing it to some()/every() would also hand it the index and array
const visible = (can: (k: PermissionKey) => boolean) => (i: NavItem) =>
  (Array.isArray(i.perm) ? i.perm.some((k) => can(k)) : can(i.perm)) && (i.alsoNeeds ?? []).every((k) => can(k)) && !i.hideIf?.some((k) => can(k));

/** First screen in the menu, for users without a dashboard (which lives at /) */
/** Every screen the user can open, in menu order, for the search palette */
export function allowedDestinations(can: (k: PermissionKey) => boolean): Destination[] {
  const out: Destination[] = [];
  for (const g of NAV) for (const i of g.items) if (visible(can)(i)) out.push({ to: i.to, label: i.label, short: i.short, icon: i.icon, group: g.group });
  return out;
}

const COLLAPSE_KEY = 'igoal.sidebar';
const TAB_PRIORITY = ['/', '/stock', '/shop-orders', '/orders', '/shipments', '/order-tracker', '/items'];

export function firstAllowedPath(can: (k: PermissionKey) => boolean): string | null {
  for (const g of NAV) for (const i of g.items) if (i.to !== '/' && visible(can)(i)) return i.to;
  return null;
}

/** Marks a scroll area with data-fade-top / data-fade-bottom while content is hidden above or below */
function useScrollFades<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      el.toggleAttribute('data-fade-top', el.scrollTop > 1);
      el.toggleAttribute('data-fade-bottom', el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    };
    // open on the current page, e.g. Settings at the bottom on a short phone screen
    el.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
    update();
    el.addEventListener('scroll', update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      resize.disconnect();
    };
  }, []);
  return ref;
}

const accountLink = 'flex w-full cursor-pointer items-center gap-2.5 px-3 py-2.5 text-sm text-ink hover:bg-ink/5';

const THEMES: { value: ThemeChoice; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { value: 'system', label: 'Auto', icon: SunMoon },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

/** Light / dark / follow the device, kept per device */
function ThemePicker() {
  const { choice, setChoice } = useTheme();
  return (
    <div className="px-3 py-2">
      <div className="mb-1 text-xs text-ink-faint">Appearance</div>
      <div role="radiogroup" aria-label="Appearance" className="grid grid-cols-3 gap-1 rounded-md bg-paper p-0.5">
        {THEMES.map((t) => (
          <button
            key={t.value}
            role="radio"
            aria-checked={choice === t.value}
            onClick={() => setChoice(t.value)}
            className={clsx(
              'flex h-8 cursor-pointer items-center justify-center gap-1 rounded-[5px] text-xs font-medium transition-colors',
              choice === t.value ? 'bg-surface text-ink shadow-sm' : 'text-ink-soft hover:text-ink',
            )}
          >
            <t.icon className="size-3.5" aria-hidden />
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Name and role at the foot of the menu; the account options open above it */
function AccountMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = () => {
    setOpen(false);
    onNavigate?.();
  };

  return (
    <div ref={ref} className="relative border-t border-white/10 px-3 py-3">
      {open && (
        <div role="menu" className="absolute right-3 bottom-full left-3 z-40 mb-1 overflow-hidden rounded-lg border border-line bg-surface-raised py-1 shadow-xl">
          <ThemePicker />
          <div className="my-1 border-t border-line" />
          <NavLink to="/account/devices" onClick={go} role="menuitem" className={accountLink}>
            <MonitorSmartphone className="size-4 text-ink-soft" /> Signed-in devices
          </NavLink>
          <NavLink to="/account/timezone" onClick={go} role="menuitem" className={accountLink}>
            <Clock className="size-4 text-ink-soft" /> Time zone and currency
          </NavLink>
          <NavLink to="/account/notifications" onClick={go} role="menuitem" className={accountLink}>
            <Bell className="size-4 text-ink-soft" /> Notifications
          </NavLink>
          <NavLink to="/account/password" onClick={go} role="menuitem" className={accountLink}>
            <KeyRound className="size-4 text-ink-soft" /> Change password
          </NavLink>
          <div className="my-1 border-t border-line" />
          <button onClick={logout} role="menuitem" className={accountLink}>
            <LogOut className="size-4 text-ink-soft" /> Sign out
          </button>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        className={clsx('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors', open ? 'bg-white/12' : 'hover:bg-white/6')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-white">{me?.fullName}</div>
          <div className="truncate text-xs text-white/50">{me?.role.name}</div>
        </div>
        <ChevronUp className={clsx('size-4 shrink-0 text-white/50 transition-transform', !open && 'rotate-180')} />
      </button>
    </div>
  );
}

function Sidebar({ onNavigate, collapsed, onToggle, onSearch }: { onNavigate?: () => void; collapsed?: boolean; onToggle?: () => void; onSearch: () => void }) {
  const { can } = useAuth();
  const navRef = useScrollFades<HTMLElement>();
  const label = (text: string, node: React.ReactNode) => (collapsed ? <Tooltip content={text}>{node}</Tooltip> : node);
  return (
    <div className="flex h-full flex-col bg-sidebar pb-[env(safe-area-inset-bottom)] text-sidebar-foreground/80">
      <div className={clsx('flex items-center gap-2.5 pt-5 pb-4', collapsed ? 'flex-col px-2' : 'px-5')}>
        <svg viewBox="0 0 32 32" className="size-7 shrink-0" aria-hidden>
          <rect width="32" height="32" rx="6" fill="#2a3846" />
          <rect x="7" y="9" width="8" height="14" fill="#0F6D68" />
          <rect x="17" y="13" width="8" height="10" fill="#F6F7F5" />
        </svg>
        {!collapsed && <span className="flex-1 text-lg font-semibold tracking-tight text-white">I-GOAL</span>}
        <NotificationBell dark />
      </div>
      <div className={clsx('mb-3', collapsed ? 'px-2' : 'px-3')}>
        {label(
          'Search',
          <button
            onClick={onSearch}
            className={clsx(
              'flex w-full cursor-pointer items-center gap-2.5 rounded-md border border-white/10 bg-white/5 text-sm text-white/60 transition-colors hover:bg-white/10 hover:text-white',
              collapsed ? 'h-9 justify-center' : 'h-9 px-2.5',
            )}
            aria-label="Search"
          >
            <Search className="size-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Search…</span>
                <kbd className="hidden rounded border border-white/15 px-1.5 py-0.5 font-sans text-[10px] text-white/50 lg:block">{isMac ? '⌘' : 'Ctrl'} K</kbd>
              </>
            )}
          </button>,
        )}
      </div>
      <nav ref={navRef} className={clsx('nav-scroll min-h-0 flex-1 overflow-y-auto', collapsed ? 'px-2' : 'pr-1.5 pl-3')} aria-label="Main">
        {NAV.map((g) => {
          const items = g.items.filter(visible(can));
          if (!items.length) return null;
          return (
            <div key={g.group ?? 'top'} className={clsx('mb-4', collapsed && 'border-t border-white/10 pt-2 first:border-0 first:pt-0')}>
              {g.group && !collapsed && <div className="px-2 pb-1.5 text-xs text-white/45">{g.group}</div>}
              {items.map((i) => (
                <Fragment key={i.label}>
                {label(
                  i.label,
                  <NavLink
                    to={i.to}
                    end={i.to === '/'}
                    onClick={onNavigate}
                    aria-label={collapsed ? i.label : undefined}
                    className={({ isActive }) =>
                      clsx(
                        'mb-0.5 flex items-center gap-2.5 rounded-md py-1.5 text-sm transition-colors max-lg:min-h-tap',
                        collapsed ? 'justify-center px-0' : 'px-2',
                        isActive ? 'bg-white/12 text-white' : 'hover:bg-white/6 hover:text-white',
                      )
                    }
                  >
                    <i.icon className="size-4 shrink-0" />
                    {!collapsed && i.label}
                  </NavLink>,
                )}
                </Fragment>
              ))}
            </div>
          );
        })}
      </nav>
      {onToggle && (
        <div className={clsx('hidden lg:block', collapsed ? 'px-2' : 'px-3')}>
          {label(
            'Expand menu',
            <button
              onClick={onToggle}
              className={clsx('flex h-8 w-full cursor-pointer items-center gap-2.5 rounded-md text-xs text-white/50 hover:bg-white/6 hover:text-white', collapsed ? 'justify-center' : 'px-2')}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            >
              {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
              {!collapsed && 'Collapse'}
            </button>,
          )}
        </div>
      )}
      {!collapsed ? (
        <AccountMenu onNavigate={onNavigate} />
      ) : (
        <CollapsedAccount />
      )}
    </div>
  );
}

/** Initials only; the full account menu needs the expanded sidebar */
function CollapsedAccount() {
  const { me } = useAuth();
  const initials = (me?.fullName ?? '?')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="border-t border-white/10 px-2 py-3">
      <Tooltip content={`${me?.fullName} · ${me?.role.name}`}>
        <NavLink to="/account/timezone" className="mx-auto flex size-8 items-center justify-center rounded-full bg-white/12 text-xs font-semibold text-white" aria-label="Account">
          {initials}
        </NavLink>
      </Tooltip>
    </div>
  );
}

/** Phone navigation: the first four screens the user can open, then “More” for the full menu */
function BottomTabs({ onMore }: { onMore: () => void }) {
  const { can } = useAuth();
  // Daily screens first (dashboard, stock, orders, shipments), then the rest in menu order
  const all = allowedDestinations(can);
  const rank = (d: Destination) => {
    const i = TAB_PRIORITY.indexOf(d.to);
    return i === -1 ? TAB_PRIORITY.length : i;
  };
  const tabs = [...all].sort((a, b) => rank(a) - rank(b)).slice(0, 4);
  if (tabs.length < 2) return null;
  const item = 'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium';
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface/95 px-[env(safe-area-inset-left)] pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
      aria-label="Quick navigation"
    >
      {tabs.map((t) => (
        <NavLink key={t.to + t.label} to={t.to} end={t.to === '/'} className={({ isActive }) => clsx(item, isActive ? 'text-teal' : 'text-ink-faint')}>
          {({ isActive }) => (
            <>
              <span className={clsx('flex h-7 w-12 items-center justify-center rounded-full', isActive && 'bg-teal-wash')}>
                <t.icon className="size-5" />
              </span>
              <span className="max-w-full truncate px-1">{t.short ?? t.label}</span>
            </>
          )}
        </NavLink>
      ))}
      <button onClick={onMore} className={clsx(item, 'cursor-pointer text-ink-faint')} aria-label="More screens">
        <span className="flex h-7 w-12 items-center justify-center rounded-full">
          <MoreHorizontal className="size-5" />
        </span>
        <span>More</span>
      </button>
    </nav>
  );
}

/** Shown for the moment a screen's code is loading */
function PageSkeleton() {
  return (
    <div role="status" aria-label="Loading">
      <Skeleton className="mb-2 h-7 w-48" />
      <Skeleton className="mb-6 h-4 w-80 max-w-full" />
      <SkeletonCards count={3} />
    </div>
  );
}

export function Layout() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        // private mode
      }
      return !c;
    });

  // Cmd/Ctrl+K opens search anywhere; "/" too, when not typing
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearch((s) => !s);
      } else if (e.key === '/' && !search) {
        const t = e.target as HTMLElement;
        if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable) return;
        e.preventDefault();
        setSearch(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [search]);

  return (
    <div className={clsx('min-h-dvh', collapsed ? 'lg:pl-16' : 'lg:pl-60')}>
      <a
        href="#main"
        className="sr-only z-[70] rounded-md bg-teal px-3 py-2 text-sm font-medium text-on-teal focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Skip to content
      </a>
      <aside className={clsx('fixed inset-y-0 left-0 hidden lg:block', collapsed ? 'w-16' : 'w-60')}>
        <Sidebar collapsed={collapsed} onToggle={toggle} onSearch={() => setSearch(true)} />
      </aside>

      <header className="sticky top-0 z-30 flex h-[calc(3rem+env(safe-area-inset-top))] items-center gap-1 border-b border-line bg-surface pt-[env(safe-area-inset-top)] pr-[max(0.75rem,env(safe-area-inset-right))] pl-[max(0.75rem,env(safe-area-inset-left))] lg:hidden">
        <button onClick={() => setOpen(true)} className="flex size-tap cursor-pointer items-center justify-center rounded-md hover:bg-ink/5" aria-label="Open menu">
          <Menu className="size-5" />
        </button>
        <span className="flex-1 pl-1 font-semibold">I-GOAL</span>
        <button onClick={() => setSearch(true)} className="flex size-tap cursor-pointer items-center justify-center rounded-md text-ink-soft hover:bg-ink/5" aria-label="Search">
          <Search className="size-5" />
        </button>
        <NotificationBell />
      </header>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-overlay" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-sidebar pt-[env(safe-area-inset-top)]">
            <button onClick={() => setOpen(false)} className="absolute top-[calc(0.75rem+env(safe-area-inset-top))] right-2 z-10 flex size-tap cursor-pointer items-center justify-center rounded-md text-white/70 hover:text-white" aria-label="Close menu">
              <X className="size-5" />
            </button>
            <Sidebar onNavigate={() => setOpen(false)} onSearch={() => { setOpen(false); setSearch(true); }} />
          </div>
        </div>
      )}

      <CommandPalette open={search} onOpenChange={setSearch} destinations={allowedDestinations(can)} />

      <main
        id="main"
        tabIndex={-1}
        className="mx-auto max-w-[1400px] pt-4 pr-[max(1rem,env(safe-area-inset-right))] pb-[calc(4.5rem+env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] outline-none sm:px-6 sm:pt-5 lg:py-7 lg:pb-7"
      >
        <Suspense fallback={<PageSkeleton />}>
          <Outlet />
        </Suspense>
      </main>

      <BottomTabs onMore={() => setOpen(true)} />
    </div>
  );
}
